import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import type { AvailableAgent } from "@/services/types/agent-gateway";
import { Loader2 } from "lucide-react";
import { type FormEvent, useEffect, useId, useState } from "react";

export interface NewAgentSessionInput {
  agentKey: string;
  sessionName: string;
  campaign?: {
    targetSegments: IcpSegment[];
    requestedCompanyCount: number;
    discoveryMode?: "new" | "refresh";
  };
}

export type IcpSegment =
  | "P1_HOSPITALITY"
  | "P2_FOOD_SERVICE"
  | "P3_PRIVATE_LABEL_INSTITUTIONAL"
  | "P4_FASHION_DESIGNER_BRANDS"
  | "P5_REAL_ESTATE_COMPANIES"
  | "P6_VEHICLE_SHOWROOMS"
  | "FOOD_DISTRIBUTOR_WHOLESALER"
  | "HORECA_SUPPLIER"
  | "FRESH_FROZEN_IMPORTER"
  | "MULTIBRANCH_GROCERY_CASH_CARRY";

const drMuellerSegmentOptions: { value: IcpSegment; label: string }[] = [
  { value: "P1_HOSPITALITY", label: "Hotels and hospitality" },
  { value: "P2_FOOD_SERVICE", label: "Food-service groups" },
  {
    value: "P3_PRIVATE_LABEL_INSTITUTIONAL",
    label: "Private-label and institutional buyers",
  },
  {
    value: "P4_FASHION_DESIGNER_BRANDS",
    label: "Fashion designer brands",
  },
  {
    value: "P5_REAL_ESTATE_COMPANIES",
    label: "Real estate companies",
  },
  {
    value: "P6_VEHICLE_SHOWROOMS",
    label: "Vehicle showrooms hosting get-togethers",
  },
];

const salesResearchSegmentOptions: { value: IcpSegment; label: string }[] = [
  {
    value: "FOOD_DISTRIBUTOR_WHOLESALER",
    label: "Food distributors and wholesalers",
  },
  { value: "HORECA_SUPPLIER", label: "HORECA suppliers" },
  {
    value: "FRESH_FROZEN_IMPORTER",
    label: "Fresh produce and frozen-food importers",
  },
  {
    value: "MULTIBRANCH_GROCERY_CASH_CARRY",
    label: "Multi-branch grocery and cash-and-carry chains",
  },
];

interface NewAgentSessionDialogProps {
  agents: AvailableAgent[];
  defaultAgentKey: string;
  open: boolean;
  isCreating: boolean;
  createError?: string;
  onOpenChange: (open: boolean) => void;
  onStartSession: (input: NewAgentSessionInput) => void;
}

const avatarColours = ["bg-[#123d08]", "bg-[#251432]", "bg-[#30364f]"];

export function NewAgentSessionDialog({
  agents,
  defaultAgentKey,
  open,
  isCreating,
  createError,
  onOpenChange,
  onStartSession,
}: NewAgentSessionDialogProps) {
  const sessionNameId = useId();
  const requestedCompanyCountId = useId();
  const discoveryModeId = useId();
  const targetSegmentsId = useId();
  const [sessionName, setSessionName] = useState("");
  const [selectedAgentKey, setSelectedAgentKey] = useState(defaultAgentKey);
  const [targetSegments, setTargetSegments] = useState<IcpSegment[]>([]);
  const [requestedCompanyCount, setRequestedCompanyCount] = useState(10);
  const [discoveryMode, setDiscoveryMode] = useState<"new" | "refresh">("new");

  useEffect(() => {
    if (open) {
      setSessionName("");
      setSelectedAgentKey(defaultAgentKey);
      setTargetSegments([]);
      setRequestedCompanyCount(10);
      setDiscoveryMode("new");
    }
  }, [defaultAgentKey, open]);

  const trimmedSessionName = sessionName.trim();
  const selectedAgent = agents.find((agent) => agent.key === selectedAgentKey);
  const requiresCampaignBrief =
    selectedAgent?.capabilities.research_campaign === true;
  const segmentOptions =
    selectedAgent?.key === "openfarming-sales-research"
      ? salesResearchSegmentOptions
      : drMuellerSegmentOptions;
  const campaignBriefIsValid =
    !requiresCampaignBrief ||
    (targetSegments.length > 0 &&
      requestedCompanyCount >= 1 &&
      requestedCompanyCount <= 100);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!trimmedSessionName || !campaignBriefIsValid || isCreating) {
      return;
    }

    onStartSession({
      agentKey: selectedAgentKey,
      sessionName: trimmedSessionName,
      campaign: requiresCampaignBrief
        ? {
            targetSegments,
            requestedCompanyCount,
            discoveryMode,
          }
        : undefined,
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!isCreating) {
          onOpenChange(nextOpen);
        }
      }}
    >
      <DialogContent
        className={cn(
          "max-h-[calc(100vh-2rem)] overflow-y-auto",
          "gap-5 rounded-xl border-0 bg-white p-7",
          "shadow-[0_12px_40px_rgba(0,0,0,0.18)]",
          "sm:max-w-[560px] sm:p-8",
          "[&>[data-slot=dialog-close]]:hidden",
        )}
        overlayClassName="bg-black/40"
      >
        <form className="grid gap-5" onSubmit={handleSubmit}>
          <DialogHeader className="gap-1.5 text-left">
            <DialogTitle className="font-medium text-2xl text-[#212529] tracking-[-0.48px]">
              New Agent Session
            </DialogTitle>
            <DialogDescription className="text-[#6c757d] text-sm">
              Name your session and choose which agent team will run it.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-1.5">
            <Label
              className="font-medium text-[#212529] text-sm"
              htmlFor={sessionNameId}
            >
              Session name
            </Label>
            <Input
              autoComplete="off"
              autoFocus
              className="h-[45px] rounded-lg border-[#ced5da] px-3.5 text-base"
              disabled={isCreating}
              id={sessionNameId}
              maxLength={255}
              onChange={(event) => setSessionName(event.target.value)}
              placeholder="e.g. August restock pricing"
              value={sessionName}
            />
          </div>

          <fieldset className="grid gap-3">
            <legend className="mb-2 font-medium text-[#212529] text-sm">
              Agent team
            </legend>

            <RadioGroup
              disabled={isCreating}
              onValueChange={(value) => {
                setSelectedAgentKey(value);
                setTargetSegments([]);
                setDiscoveryMode("new");
              }}
              value={selectedAgentKey}
            >
              {agents.map((agent) => {
                const selected = agent.key === selectedAgentKey;
                const agentCount = agent.team_size ?? 3;

                return (
                  <label
                    className={cn(
                      "flex min-h-[74px] cursor-pointer items-center gap-3.5",
                      "rounded-lg border px-4 py-3.5 transition-colors",
                      selected
                        ? "border-2 border-brand-darkGreen bg-[rgba(16,45,3,0.04)]"
                        : "border-[#ced5da] bg-white hover:border-brand-darkGreen/50",
                    )}
                    htmlFor={`agent-team-${agent.key}`}
                    key={agent.key}
                  >
                    <RadioGroupItem
                      className="size-[18px] border-[#ced5da] text-brand-darkGreen data-[state=checked]:border-brand-darkGreen [&_svg]:fill-brand-darkGreen"
                      id={`agent-team-${agent.key}`}
                      value={agent.key}
                    />

                    <span className="min-w-0 flex-1">
                      <span className="block font-medium text-[#212529] text-base">
                        {agent.display_name}
                      </span>
                      <span className="mt-0.5 block text-[#6c757d] text-sm">
                        {agent.description ??
                          `Run a new session with ${agent.display_name}`}
                      </span>
                    </span>

                    <span className="shrink-0 text-right">
                      <span aria-hidden="true" className="flex justify-end">
                        {avatarColours
                          .slice(0, Math.min(agentCount, avatarColours.length))
                          .map((colour, index) => (
                            <Avatar
                              className={cn(
                                "size-6 border-2 border-white",
                                index > 0 && "-ml-2",
                              )}
                              key={colour}
                            >
                              <AvatarFallback className={colour} />
                            </Avatar>
                          ))}
                      </span>
                      <span className="mt-1 block text-[#6c757d] text-sm">
                        {agentCount} agents
                      </span>
                    </span>
                  </label>
                );
              })}
            </RadioGroup>
          </fieldset>

          {requiresCampaignBrief ? (
            <fieldset className="grid gap-3">
              <legend className="font-medium text-[#212529] text-sm">
                Target company segments
              </legend>
              <p className="text-[#6c757d] text-sm">
                {selectedAgent?.key === "openfarming-sales-research"
                  ? "Research will cover Saudi Arabia and the UAE, researching companies and current decision-makers only."
                  : "Research will cover every UAE emirate and qualify accounts at a fixed score of 40."}
              </p>
              {segmentOptions.map((option) => (
                <label
                  className="flex items-center gap-2.5 text-[#212529] text-sm"
                  htmlFor={`${targetSegmentsId}-${option.value}`}
                  key={option.value}
                >
                  <Checkbox
                    checked={targetSegments.includes(option.value)}
                    disabled={isCreating}
                    id={`${targetSegmentsId}-${option.value}`}
                    onCheckedChange={(checked) =>
                      setTargetSegments((current) =>
                        checked
                          ? [...current, option.value]
                          : current.filter((value) => value !== option.value),
                      )
                    }
                  />
                  {option.label}
                </label>
              ))}
              <div className="grid gap-1.5 pt-1">
                <Label htmlFor={requestedCompanyCountId}>
                  Number of companies
                </Label>
                <Input
                  className="h-[45px] rounded-lg border-[#ced5da] px-3.5 text-base"
                  disabled={isCreating}
                  id={requestedCompanyCountId}
                  max={100}
                  min={1}
                  onChange={(event) =>
                    setRequestedCompanyCount(
                      Number.isNaN(event.currentTarget.valueAsNumber)
                        ? 0
                        : event.currentTarget.valueAsNumber,
                    )
                  }
                  type="number"
                  value={requestedCompanyCount}
                />
              </div>
              <fieldset className="grid gap-2 pt-1">
                <legend className="font-medium text-[#212529] text-sm">
                  Discovery mode
                </legend>
                <RadioGroup
                  disabled={isCreating}
                  onValueChange={(value) =>
                    setDiscoveryMode(value as "new" | "refresh")
                  }
                  value={discoveryMode}
                >
                  <label
                    className="flex items-start gap-2.5 text-[#212529] text-sm"
                    htmlFor={`${discoveryModeId}-new`}
                  >
                    <RadioGroupItem
                      id={`${discoveryModeId}-new`}
                      value="new"
                    />
                    <span>
                      New research
                      <span className="block text-[#6c757d]">
                        Exclude rejected and active companies; reuse eligible
                        saved dossiers.
                      </span>
                    </span>
                  </label>
                  <label
                    className="flex items-start gap-2.5 text-[#212529] text-sm"
                    htmlFor={`${discoveryModeId}-refresh`}
                  >
                    <RadioGroupItem
                      id={`${discoveryModeId}-refresh`}
                      value="refresh"
                    />
                    <span>
                      Refresh existing research
                      <span className="block text-[#6c757d]">
                        Re-research matching companies while preserving
                        active-session and confirmed-outreach protection.
                      </span>
                    </span>
                  </label>
                </RadioGroup>
              </fieldset>
            </fieldset>
          ) : null}

          {createError ? (
            <p
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700 text-sm"
              role="alert"
            >
              {createError}
            </p>
          ) : null}

          <DialogFooter className="gap-3">
            <Button
              className="h-[45px] rounded-lg border-[#ced5da] px-5 text-base"
              disabled={isCreating}
              onClick={() => onOpenChange(false)}
              type="button"
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              className="h-[45px] rounded-lg bg-brand-darkGreen px-5 text-base text-white hover:bg-brand-darkGreen/90"
              disabled={
                !trimmedSessionName || !campaignBriefIsValid || isCreating
              }
              type="submit"
            >
              {isCreating ? (
                <>
                  <Loader2 aria-hidden="true" className="animate-spin" />
                  Starting…
                </>
              ) : (
                "Start Session"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
